// src/components/resumo/ImportApostilaModal.tsx

import React, { useState, useRef } from 'react';
import { Upload, FileCheck, AlertTriangle, X } from 'lucide-react';

interface ApostilaFileEntry {
    file: File;
    data?: any;
    error?: string;
}

interface ImportApostilaModalProps {
    onImport: (dataList: any[]) => void;
    onClose: () => void;
    darkMode?: boolean;
    /** Permite selecionar e importar vários arquivos .graia de uma vez. Padrão: true. */
    multiple?: boolean;
}

const ImportApostilaModal: React.FC<ImportApostilaModalProps> = ({
    onImport,
    onClose,
    darkMode = false,
    multiple = true
}) => {
    const [dragOver, setDragOver] = useState(false);
    const [entries, setEntries] = useState<ApostilaFileEntry[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const parseFile = async (f: File): Promise<ApostilaFileEntry> => {
        if (!f.name.endsWith('.graia')) {
            return { file: f, error: 'Selecione um arquivo .graia válido' };
        }
        try {
            const text = await f.text();
            const data = JSON.parse(text);
            if (data.__type !== 'graia-apostila') {
                return { file: f, error: 'Arquivo inválido: não é uma apostila GRAIA' };
            }
            return { file: f, data };
        } catch {
            return { file: f, error: 'Erro ao ler o arquivo. Verifique se está corrompido.' };
        }
    };

    const handleFiles = async (fileList: FileList | File[]) => {
        const files = Array.from(fileList);
        if (!files.length) return;
        const parsed = await Promise.all(files.map(parseFile));
        setEntries(prev => multiple ? [...prev, ...parsed] : parsed.slice(0, 1));
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOver(false);
        if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
    };

    const removeEntry = (index: number) => {
        setEntries(prev => prev.filter((_, i) => i !== index));
    };

    const validEntries = entries.filter(e => e.data);

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 99999
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: darkMode ? '#0f1913' : '#fff',
                    border: `1px solid ${darkMode ? 'rgba(95,186,97,0.3)' : '#e5e7eb'}`,
                    borderRadius: 16,
                    padding: 32,
                    width: '100%',
                    maxWidth: 480,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: darkMode ? '#E8F5E9' : '#111827' }}>
                        Importar Apostila{multiple ? 's' : ''}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: darkMode ? '#A5D6A7' : '#6b7280', padding: 4
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Drop Zone */}
                <div
                    onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                        border: `2px dashed ${dragOver
                            ? 'var(--platform-accent-light)'
                            : darkMode ? 'var(--platform-a25)' : '#d1d5db'}`,
                        borderRadius: 12,
                        padding: '32px 24px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        background: dragOver
                            ? (darkMode ? 'var(--platform-a15)' : 'var(--platform-a05)')
                            : (darkMode ? 'rgba(255,255,255,0.03)' : '#f9fafb'),
                        transition: 'all 0.2s ease',
                        marginBottom: 16
                    }}
                >
                    <Upload size={32} style={{ color: 'var(--platform-accent-light)', marginBottom: 12 }} />
                    <p style={{ margin: '0 0 4px', fontWeight: 600, color: darkMode ? '#E8F5E9' : '#374151' }}>
                        Arraste {multiple ? 'arquivos' : 'um arquivo'} .graia aqui
                    </p>
                    <p style={{ margin: 0, fontSize: 13, color: darkMode ? '#A5D6A7' : '#6b7280' }}>
                        ou clique para selecionar
                    </p>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".graia"
                        multiple={multiple}
                        style={{ display: 'none' }}
                        onChange={e => {
                            if (e.target.files?.length) handleFiles(e.target.files);
                            e.target.value = '';
                        }}
                    />
                </div>

                {/* Lista de arquivos selecionados */}
                {entries.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16, maxHeight: 240, overflowY: 'auto' }}>
                        {entries.map((entry, index) => (
                            <div
                                key={`${entry.file.name}-${entry.file.size}-${index}`}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '10px 12px', borderRadius: 10,
                                    background: entry.error
                                        ? '#fee2e2'
                                        : (darkMode ? 'rgba(47,113,48,0.15)' : '#f0fdf4'),
                                    border: `1px solid ${entry.error ? '#fecaca' : (darkMode ? 'rgba(95,186,97,0.3)' : '#bbf7d0')}`
                                }}
                            >
                                {entry.error
                                    ? <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0 }} />
                                    : <FileCheck size={16} style={{ color: 'var(--platform-accent-light)', flexShrink: 0 }} />}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: 600, fontSize: 13,
                                        color: darkMode ? '#E8F5E9' : '#111827',
                                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                                    }}>
                                        {entry.data?.title || entry.file.name}
                                    </div>
                                    <div style={{ fontSize: 12, color: entry.error ? '#dc2626' : (darkMode ? '#A5D6A7' : '#6b7280') }}>
                                        {entry.error || `${entry.data?.pages?.length || 0} página(s) · ${Object.keys(entry.data?.quizzes || {}).length} quiz(zes)`}
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeEntry(index)}
                                    title="Remover"
                                    style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: darkMode ? '#A5D6A7' : '#6b7280', padding: 2, flexShrink: 0
                                    }}
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Aviso */}
                <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    padding: '10px 14px', borderRadius: 8,
                    background: darkMode ? 'rgba(234,179,8,0.1)' : '#fef9c3',
                    color: darkMode ? '#fde047' : '#92400e',
                    fontSize: 12, marginBottom: 20, lineHeight: 1.5
                }}>
                    <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                    {multiple
                        ? 'Cada arquivo válido vira uma nova apostila. Arquivos inválidos serão ignorados.'
                        : 'Importar substituirá o conteúdo atual da apostila. Esta ação não pode ser desfeita.'}
                </div>

                {/* Ações */}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        style={{
                            padding: '9px 20px', borderRadius: 8, cursor: 'pointer',
                            background: 'none', border: `1px solid ${darkMode ? 'rgba(95,186,97,0.3)' : '#e5e7eb'}`,
                            color: darkMode ? '#A5D6A7' : '#374151', fontWeight: 600, fontSize: 14
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={() => validEntries.length > 0 && onImport(validEntries.map(e => e.data))}
                        disabled={!validEntries.length}
                        style={{
                            padding: '9px 20px', borderRadius: 8, cursor: validEntries.length ? 'pointer' : 'not-allowed',
                            background: validEntries.length ? 'var(--platform-accent)' : '#9ca3af',
                            border: 'none', color: 'white', fontWeight: 600, fontSize: 14,
                            transition: 'background 0.2s'
                        }}
                    >
                        {validEntries.length > 1 ? `Importar ${validEntries.length} Apostilas` : 'Importar Apostila'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportApostilaModal;
